<template>
  <div class="flex flex-col">
    <p
      v-if="inputLabel"
      class="flex items-start font-semibold text-gray-500 dark:text-gray-400 mb-2
        w-full flex-auto text-left select-none capitalize
      "
      :class="`text-${size}`"
    >
      {{ inputLabel }}<span v-if="required" class="text-red-500"> *</span>
      <FloatingTooltip v-if="tooltip || $slots.tooltip" placement="top" class="ml-2">
        <template #button>
          <font-awesome-icon
            icon="fa-solid fa-circle-question"
            class="text-primary dark:text-primary-200 dark:hover:text-primary-100 hover:text-primary-500 dark:hover:shadow-primarydark/30 cursor-pointer transition"
          />
        </template>

        <template #tooltip>
          <!-- Prefer slot, fallback to string -->
          <slot name="tooltip">
            {{ tooltip }}
          </slot>
        </template>
      </FloatingTooltip>
    </p>
    <div class="flex items-center relative">
      <input
        :type="computedInputType"
        :value="modelValue"
        @input="handleInput"
        @blur="$emit('blur')"
        @keyup.enter="$emit('submit')"
        @paste="handlePaste"
        class="w-full rounded h-10 focus:ring-1 focus:ring-primary focus:border focus:border-primary text_input"
        :maxlength="maxlength"
        :class="inputClasses"
        :required="required"
        :placeholder="placeholder"
        :disabled="disabled"
        :autocapitalize="lowercase ? 'off' : null"
        :lowercase="lowercase"
        :name="name"
        :autocomplete="autocomplete"
        ref="input"
      >
      <span v-if="units" class="font-semibold ml-2">{{ units }}</span>
    </div>
    <p v-if="!valid" class="text-red-700 font-medium text-sm text-left mt-1">
      {{ validationMessage }}
    </p>
  </div>
</template>

<script>
import FloatingTooltip from "@/components/Tooltips/FloatingTooltip.vue"

export default {
  name: "TextInput",
  components: {
    FloatingTooltip,
  },
  props: {
    modelValue: null,
    inputLabel: {
      type: String,
    },
    inputType: {
      type: String,
      required: false,
      default: "text"
    },
    placeholder: {
      type: String,
    },
    outlined: {
      type: Boolean,
    },
    filled: {
      type: Boolean,
    },
    size: {
      type: String,
      default: "md",
    },
    required: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: String,
      required: false,
    },
    uppercase: {
      type: Boolean,
      default: false,
    },
    lowercase: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: String,
      required: false,
    },
    units: {
      type: String,
      required: false,
    },
    valid: {
      type: Boolean,
      required: false,
      default: true
    },
    validationMessage: {
      type: String,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      required: false
    },
    autocomplete: {
      type: String,
      required: false
    }
  },
  emits: ["inputCustom", "update:modelValue", "submit", "blur"],
  data() {
    return {
      showPW: false,
      clickedOnTooltip: false
    }
  },
  computed: {
    inputClasses() {
      return {
        "border border-gray-300 dark:border-gray-800": this.outlined && this.valid,
        "border border-red-700": this.outlined && !this.valid,
        "bg-gray-100": this.filled || this.disabled,
        uppercase: this.uppercase,
        lowercase: this.lowercase,
        "p-2 text-sm": this.size === "sm",
        "p-2": this.size === "md",
        "p-4": this.size === "lg",
      };
    },
    computedInputType() {
      if (this.inputType === 'password') return this.showPW ? 'text' : 'password'
      else return this.inputType
    }
  },
  async mounted() {
    if (this.autofocus) {
      await this.$nextTick()
      this.$refs.input.select()
    }
  },
  methods: {
    togglePW() {
      this.showPW = !this.showPW
    },
    toggleTooltip() {
      this.clickedOnTooltip = !this.clickedOnTooltip
    },
    handleInput(event) {
      let newVal = event.target.value
      if (this.lowercase) newVal = newVal.toLowerCase()
      if (this.uppercase) newVal = newVal.toUpperCase()
      this.$emit("update:modelValue", newVal)
    },
    async handlePaste() {
      navigator.clipboard
        .readText()
        .then((clipText) => {
          this.$emit("update:modelValue", clipText)
      })
    },
  }
};
</script>

<style scoped>
.text_input::placeholder{
  opacity: 0.5
}
/* disable zoom on input on mobile */
@supports (-webkit-overflow-scrolling: touch) {
  .nozoom {
    font-size: 16px;
  }
}
</style>